import { useMemo } from "react";
import { Box, Flex, Icon, Spinner, Stack, Text } from "@storyofams/react-ui";
import { css } from "styled-components";
import { useShopify } from "~context";
import { showToast } from "~lib";
import { Button } from "~components/common/Button";
import { NhCartWhite } from "~components/common/Icons";
import { MidStroke } from "~components/common/Story/components/MidStroke";
import { MidStroke2 } from "~components/common/Story/components/MidStroke2";
import { AddToWishlistRecipe } from "~components/product/components/AddToWishlistRecipe";
import { useRecipeCart } from "./CartProvider";

export const TotalInfo = ({ id, bundleId, recipe, ...props }) => {
  const { ingredientsToCart, toolsToCart, toolsPrice, mainProducts } =
    useRecipeCart();
  const {
    addItemToCart,
    updatingCart,
    toggleCartOverlay,
    cartAttributesUpdate,
    removeLineItem,
    customerCompanyId,
    isB2B,
    cart,
  } = useShopify();
  const maxQuantity = useMemo(() => {
    const ratios = ingredientsToCart
      .map((item) => {
        const volume = item?.variantTitle?.replace(/[^\d]/g, "");
        return {
          ratio: Number(volume) / item.gram,
          optional: item.optional,
        };
      })
      .filter((item) => item.ratio)
      .sort((a, b) => a.ratio - b.ratio);
    const min = ratios.find(({ optional }) => !optional);
    return Math.floor(Number(min?.ratio?.toFixed(2))) || 1;
  }, [ingredientsToCart]);
  const minValue = `${maxQuantity} x ${recipe?.makes_uom?.text}  (${recipe?.contents_uom?.text})`;
  const isDiscounted = useMemo(() => {
    if (ingredientsToCart.length < mainProducts.length) return false;
    return mainProducts?.every((item) =>
      ingredientsToCart.find(
        (prod) => prod.id === item.id || item.alt.includes(prod.id)
      )
    );
  }, [ingredientsToCart, mainProducts]);
  const priceIngredients = ingredientsToCart.reduce(
    (acc, item) => acc + Number(item.price),
    0
  );
  const totalPricePer = !isNaN(Number(priceIngredients) / maxQuantity)
    ? (Number(priceIngredients) / maxQuantity).toFixed(2)
    : 0;
  const discountValue = recipe.recipe_discount;
  const price =
    discountValue && isDiscounted
      ? (Number(priceIngredients) * (1 - discountValue / 100)).toFixed(2)
      : priceIngredients?.toFixed(2);
  const pricePer =
    discountValue && isDiscounted
      ? (Number(totalPricePer) * (1 - discountValue / 100)).toFixed(2)
      : Number(totalPricePer)?.toFixed(2);
  const recipeHandle = async () => {
    if (!ingredientsToCart.length && !toolsToCart.length) return;
    const oldRecipe = cart?.lines?.edges.find(
      (item) => item.node.merchandise.id === bundleId
    );
    if (oldRecipe) await removeLineItem(oldRecipe.node.id);
    addItemToCart(
      [...ingredientsToCart.map(({ id }) => id), ...toolsToCart].map(
        (product) => ({
          quantity: 1,
          merchandiseId: product,
        })
      ),
      () => {
        toggleCartOverlay(true);
        cartAttributesUpdate(`recipe`, {
          ids: ingredientsToCart.map((item) => {
            if (item.id.includes("gid")) return item;
            else return Buffer.from(item.id, "base64").toString();
          }),
          recipe: bundleId,
          quantity: 1,
          discount: isDiscounted && discountValue ? discountValue : 0,
        });
      }
    ).catch(() =>
      showToast(
        "Er ging iets fout bij het toevoegen van producten aan het winkelmandje!",
        "error"
      )
    );
  };
  // useEffect(() => {
  //   return () => {
  //     if (
  //       content.isDiscounted &&
  //       !isAlerted &&
  //       content?.discount &&
  //       ingredientsToCart &&
  //       ingredientsToCart.length
  //     ) {
  //       setIsAlerted(true);
  //       setTimeout(
  //         () =>
  //           alert(
  //             "Alle verplichte ingredienten moeten aangevinkt zijn om van de korting gebruik te maken. Je kunt het ingredient weer aanvinken als je de korting wilt gebruiken."
  //           ),
  //         100
  //       );
  //     }
  //   };
  // }, [ingredientsToCart]);
  return (
    <Box
      px={2}
      py={["8px", 2]}
      mt={["14px", "14px"]}
      width={"100%"}
      backgroundColor={"#FCF9F7"}
      border={"1px solid #E2DFDF"}
      borderRadius={["none", "8px"]}
      borderTopLeftRadius={"8px"}
      borderTopRightRadius={"8px"}
      position={["fixed", "inherit"]}
      transform="translate3d(0,0,0)"
      bottom={"0px"}
      zIndex={100}
      left={"0px"}
      css={{
        "-webkit-overflow-scrolling": "touch",
      }}
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      {...props}
    >
      <Box width={"100%"} position={"relative"}>
        {isDiscounted && discountValue ? (
          <Box
            textAlign={"center"}
            mb={"10px"}
            width={"max-content"}
            mx={"auto"}
          >
            <Text
              lineHeight={"19.6px"}
              fontSize={"14px"}
              color="#767473"
              mb={0.5}
            >
              Totaalprijs ingrediënten:
            </Text>
            <Box
              width={"100%"}
              position={"relative"}
              display={"flex"}
              justifyContent={"center"}
              fontSize={"18px"}
              lineHeight={"25.2px"}
            >
              <Text
                position={"relative"}
                zIndex={2}
                color={"#1D1D1B"}
                fontWeight={500}
                mr={1}
              >{`€${price || "0.00"}`}</Text>
              <Text
                color={"#767473"}
                position={"relative"}
                zIndex={2}
                textDecoration={"line-through"}
              >{`€${priceIngredients.toFixed(2)}`}</Text>
              <MidStroke right={3} top={-1} />
            </Box>
          </Box>
        ) : (
          <Box display={"flex"} mb={"10px"} width={"max-content"} mx={"auto"}>
            <Text
              lineHeight={"19.6px"}
              fontSize={"14px"}
              color="#767473"
              whiteSpace={"nowrap"}
              position={"relative"}
              zIndex={2}
              mr={2}
            >
              Totaalprijs ingrediënten:
            </Text>
            <Box position={"relative"}>
              {" "}
              <Text
                position={"relative"}
                zIndex={2}
                color={"#1D1D1B"}
                fontSize={"18px"}
                fontWeight={500}
              >{`€${price || "0.00"}`}</Text>
              <MidStroke2 top={"-4px"} left={-7} />
            </Box>
          </Box>
        )}
        <Stack width={"100%"} mt={["6px", "10px"]} space={1}>
          <Button
            justifyContent={"center"}
            width={"100%"}
            disabled={!!customerCompanyId && isB2B}
            variant="primary"
            backgroundColor={"black"}
            color="white"
            borderRadius={"6px"}
            customHeight={"57px"}
            customPY={0}
            onClick={recipeHandle}
            display={"flex"}
            as="button"
            opacity={!ingredientsToCart.length && !toolsToCart.length ? 0.5 : 1}
            cursor={
              !ingredientsToCart.length && !toolsToCart.length
                ? "not-allowed"
                : "pointer"
            }
          >
            {updatingCart ? (
              <Spinner></Spinner>
            ) : (
              <Flex>
                <Icon
                  mr={2}
                  fontSize={"20px"}
                  color="white"
                  icon={NhCartWhite}
                />
                <Text lineHeight={"25px"} fontWeight={500} fontSize={"18px"}>
                  {`Bestel nu! - €${
                    (Number(price) + Number(toolsPrice)).toFixed(2) || "0.00"
                  }`}
                </Text>
              </Flex>
            )}
          </Button>
          <AddToWishlistRecipe id={id} />
        </Stack>
        <Flex width={"100%"} mt="10px" fontSize={"14px"} lineHeight={"19.6px"}>
          <Box width={"33.3%"} textAlign={"center"} px={"4px"}>
            <Text color="#767473">Maakt maximaal</Text>
            <Text fontWeight={500}>{minValue}</Text>
          </Box>
          <Box
            borderX={"1px solid #E2DFDF"}
            width={"33.3%"}
            px={"4px"}
            textAlign={"center"}
          >
            <Text color="#767473">Prijs per {recipe?.makes_uom?.text}</Text>
            <Text fontWeight={500}>€{pricePer}</Text>
          </Box>
          <Box width={"33.3%"} textAlign={"center"} px={"4px"}>
            <Text color="#767473">Tools</Text>
            <Text fontWeight={500}>{`€${toolsPrice || "0.00"}`}</Text>
          </Box>
        </Flex>
        {isDiscounted && !!discountValue && (
          <Box
            position="absolute"
            width={["67px"]}
            height={["63px"]}
            css={css({
              backgroundImage:
                "url(https://a.storyblok.com/f/125270/205x192/c5e62cf1ae/recipe-discount-bubble.png)",
              backgroundSize: "100% 100%",
            })}
            top={-5}
            right={[0, -2]}
            pl={[1, "12px"]}
            zIndex={2}
            display="flex"
            flexDirection="column"
            fontFamily={"DINPro"}
            pt={"10px"}
          >
            <Text
              width="max-content"
              as="span"
              lineHeight="26.6px"
              fontFamily="heading"
              fontSize={["19px"]}
              fontWeight={700}
              css={{ textTransform: "uppercase" }}
            >
              -{discountValue}%
            </Text>
            <Text
              as="span"
              width="max-content"
              fontSize={"12px"}
              lineHeight="16.8px"
              letterSpacing={"0.02em"}
              fontFamily="heading"
              fontWeight={700}
              css={{ textTransform: "uppercase" }}
            >
              korting
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};
